import * as ToastPrimitive from '@radix-ui/react-toast'
import { CheckCircle2Icon, XIcon } from 'lucide-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { AvisadorContexto, type Avisar } from '@/components/ui/avisador-contexto'

/**
 * Avisos del sistema: "Aprobada · Deshacer", "Reprogramada al 24 de octubre ·
 * Deshacer" (§7.2.3, §9). Un aviso a la vez, abajo y centrado, con acción
 * opcional que dura lo que dura el aviso (10 s para Deshacer).
 */
interface Aviso {
  id: number
  texto: string
  accion?: { etiqueta: string; alHacer: () => void }
  duracionMs: number
}

export function AvisadorProveedor({ children }: { children: ReactNode }) {
  const [aviso, setAviso] = useState<Aviso | null>(null)
  const contador = useRef(0)

  const avisar = useCallback<Avisar>((texto, opciones) => {
    contador.current += 1
    setAviso({
      id: contador.current,
      texto,
      duracionMs: opciones?.duracionMs ?? (opciones?.accion ? 10_000 : 4_000),
      ...(opciones?.accion ? { accion: opciones.accion } : {}),
    })
  }, [])

  const valor = useMemo(() => avisar, [avisar])

  return (
    <AvisadorContexto.Provider value={valor}>
      <ToastPrimitive.Provider
        swipeDirection="down"
        duration={aviso?.duracionMs ?? 4_000}
      >
        {children}
        {aviso && (
          <ToastPrimitive.Root
            key={aviso.id}
            onOpenChange={(abierto) => {
              if (!abierto) setAviso(null)
            }}
            className={cn(
              'flex items-center gap-3 rounded-2xl bg-tinta px-4 py-3 text-sm text-white shadow-tarjeta',
              'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-4',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
            )}
          >
            <CheckCircle2Icon className="size-5 shrink-0" aria-hidden />
            <ToastPrimitive.Description className="flex-1">
              {aviso.texto}
            </ToastPrimitive.Description>
            {aviso.accion && (
              <ToastPrimitive.Action
                altText={aviso.accion.etiqueta}
                onClick={aviso.accion.alHacer}
                className="min-h-9 rounded-full px-2 font-medium underline underline-offset-4 hover:opacity-80"
              >
                {aviso.accion.etiqueta}
              </ToastPrimitive.Action>
            )}
            <ToastPrimitive.Close
              aria-label="Cerrar"
              className="inline-flex size-9 items-center justify-center rounded-full hover:bg-white/10"
            >
              <XIcon className="size-4" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        )}
        <ToastPrimitive.Viewport
          className={cn(
            'fixed bottom-0 left-1/2 z-[60] flex w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 flex-col p-4',
            // Deja lugar a la barra inferior fija de celular (§5.1).
            'pb-[calc(5.5rem+env(safe-area-inset-bottom))] sm:pb-6',
          )}
        />
      </ToastPrimitive.Provider>
    </AvisadorContexto.Provider>
  )
}
